import type { RefObject } from "react";
import { useEffect } from "react";

const VIRTUAL_HOLDER_SELECTOR = ".ant-table-tbody-virtual-holder";
const VIRTUAL_SCROLLBAR_SELECTOR = ".ant-table-tbody-virtual-scrollbar";

/**
 * Cancels rc-virtual-list's spurious "scroll jump" when the top row is expanded.
 *
 * rc-virtual-list renders `expandedRowRender` content inside the parent row's measured
 * virtual item, so expanding the topmost-visible row makes that single item's height jump
 * from the ~`itemHeight` estimate to the full expanded height. Its anti-jump correction
 * (List.js `syncScrollTop`) then adds `realHeight - itemHeight` to `scrollTop` — meant to
 * preserve position when you scroll up into a taller-than-estimated row, but wrong here:
 * the row grew in place, so it scrolls the freshly expanded content out of view. It fires
 * once, asynchronously, when the expanded child re-measures after its data loads.
 *
 * The correction is a *programmatic* `scrollTop` change with no user gesture behind it, and
 * it can re-fire: the expanded child re-measures several times (skeleton → data → settling),
 * so syncScrollTop runs once per re-measure. We therefore keep undoing every gesture-less
 * scroll change. A user-driven scroll, by contrast, always emits its gesture event (wheel /
 * touch / key / scrollbar pointerdown) *before* the resulting `scroll` event — that gesture
 * disarms us for good, leaving all subsequent scrolling (including rc-virtual-list's own
 * legitimate anti-jump behaviour) completely untouched. Undo the correction repeatedly;
 * stop the moment the user takes control.
 */
export function usePinScrollOnExpand(containerRef: RefObject<HTMLElement | null>, isExpanded: boolean): void {
    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        const container = containerRef.current;
        const holder = container?.querySelector<HTMLElement>(VIRTUAL_HOLDER_SELECTOR);
        if (!holder) {
            return;
        }

        const scrollbar = container?.querySelector<HTMLElement>(VIRTUAL_SCROLLBAR_SELECTOR);

        const pinnedTop = holder.scrollTop;
        const controller = new AbortController();
        const { signal } = controller;

        // A genuine user gesture means the user is taking control of scrolling: stop pinning
        // for good and leave all subsequent scrolling untouched.
        const disarm = () => controller.abort();

        const handleScroll = () => {
            if (holder.scrollTop !== pinnedTop) {
                // No gesture preceded this scroll change, so it is rc-virtual-list's spurious
                // expand correction — which re-fires across re-measures, so keep undoing it.
                holder.scrollTop = pinnedTop;
            }
        };

        holder.addEventListener("wheel", disarm, { passive: true, signal });
        holder.addEventListener("touchstart", disarm, { passive: true, signal });
        holder.addEventListener("touchmove", disarm, { passive: true, signal });
        holder.addEventListener("keydown", disarm, { signal });
        scrollbar?.addEventListener("pointerdown", disarm, { signal });
        holder.addEventListener("scroll", handleScroll, { passive: true, signal });

        return disarm;
    }, [containerRef, isExpanded]);
}
